
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import CustomSelect from "../components/CustomSelect";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import PinAccessDialog from "../components/PinAccessDialog";
import InvoiceItemsEditor from "../components/invoices/InvoiceItemsEditor";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import { getAllClients, getClientById } from "../services/ClientsService";
import { getUserId } from "../services/authService";
import { getCachedPinAccess } from "../utils/pinAccessCache";
import {
  createInvoice,
  deleteInvoice,
  getAllOwnerInvoices,
  getInvoiceById,
  updateInvoice,
} from "../services/InvoicesService";
import { getAllServices } from "../services/ServicesService";
import { getAllVenues } from "../services/VenueService";

type AdminTab = "clients" | "finance" | "expenses" | "reports";

interface AdminFinanceRow {
  id: string;
  Invoice_number?: string;
  client_name?: string;
  mobile?: string;
  invoice_date?: string;
  created_at?: string;
  venue_name?: string;
  venue_id?: string | number;
  branch?: string;
  status?: string;
  method?: string;
  services?: string;
  total_amount?: string | number;
  net_amount?: string | number;
  notes?: string;
  business_id?: string | number;
  invoice?: any;
  [key: string]: any;
}

type InvoiceClientOption = {
  id: string;
  name: string;
  email?: string;
  mobile?: string;
};

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");

const STATUS_FORM_VALUE_MAP: Record<
  1 | 2 | 3,
  "paid" | "unpaid" | "cancelled"
> = {
  1: "paid",
  2: "unpaid",
  3: "cancelled",
};

const STATUS_LABEL_MAP: Record<1 | 2 | 3, string> = {
  1: "Paid",
  2: "Unpaid",
  3: "Cancelled",
};

const PAYMENT_METHOD_LABEL_MAP: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "Cash",
  1: "Card",
  2: "Cash",
  3: "Bank Transfer",
  4: "Online Payment",
};

const resolveStatusLabel = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const statusId = Number(
    entry?.status_id ??
      invoice?.status_id ??
      invoice?.status?.id ??
      entry?.status?.id
  );
  if (Number.isFinite(statusId)) {
    const label = STATUS_LABEL_MAP[statusId as 1 | 2 | 3];
    if (label) {
      return label;
    }
  }

  const statusString = pickNonEmptyString(
    entry?.status,
    invoice?.status,
    invoice?.status_name,
    invoice?.status_label
  );

  if (statusString) {
    return toTitleCase(statusString);
  }

  if (typeof entry?.status_id === "string") {
    return toTitleCase(entry.status_id);
  }

  return "Unpaid";
};

const resolveStatusFormValue = (
  status: unknown,
  fallbackId?: unknown
): "paid" | "unpaid" | "cancelled" => {
  const numeric = Number(status ?? fallbackId);
  if (Number.isFinite(numeric)) {
    const formValue = STATUS_FORM_VALUE_MAP[numeric as 1 | 2 | 3];
    if (formValue) {
      return formValue;
    }
  }

  if (typeof status === "string" && status.trim()) {
    const normalized = status.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized;
    }
  }

  if (typeof fallbackId === "string" && fallbackId.trim()) {
    const normalized = fallbackId.trim().toLowerCase();
    if (
      normalized === "paid" ||
      normalized === "unpaid" ||
      normalized === "cancelled"
    ) {
      return normalized as "paid" | "unpaid" | "cancelled";
    }
  }

  return "unpaid";
};
const resolvePaymentMethodLabel = (entry: any): string => {
  const invoice = entry?.invoice ?? {};
  const methodId = Number(
    entry?.payment_method_id ??
      invoice?.payment_method_id ??
      invoice?.payment_method?.id
  );
  if (Number.isFinite(methodId)) {
    const label = PAYMENT_METHOD_LABEL_MAP[methodId as 0 | 1 | 2 | 3 | 4];
    if (label) {
      return label;
    }
  }

  return "Cash";
};

const normalizeInvoiceRow = (
  item: any,
  fallbackBranch?: string
): AdminFinanceRow => {
  const invoice = item?.invoice ?? {};
  const statusIdCandidate =
    item?.status_id ??
    invoice?.status_id ??
    invoice?.status?.id ??
    item?.status?.id;
  const paymentMethodIdCandidate =
    item?.payment_method_id ??
    invoice?.payment_method_id ??
    invoice?.payment_method?.id;

  const statusIdNumeric = Number(statusIdCandidate);
  const paymentMethodIdNumeric = Number(paymentMethodIdCandidate);

  const enriched = {
    ...item,
    status_id: Number.isFinite(statusIdNumeric)
      ? statusIdNumeric
      : statusIdCandidate ?? item?.status_id,
    payment_method_id: Number.isFinite(paymentMethodIdNumeric)
      ? paymentMethodIdNumeric
      : paymentMethodIdCandidate ?? item?.payment_method_id,
  };

  const branchName =
    pickNonEmptyString(
      item?.branch_name,
      item?.branchName,
      item?.branch?.name,
      item?.business_name,
      item?.business?.name,
      item?.branch,
      invoice?.branch_name,
      invoice?.branch,
      fallbackBranch
    ) || "Unknown Branch";

  const venueName =
    pickNonEmptyString(
      item?.venue_name,
      item?.venue?.name,
      invoice?.venue_name,
      invoice?.venue?.name
    ) || "";
  const venueIdCandidate =
    item?.venue_id ??
    item?.venueId ??
    item?.venue?.id ??
    item?.venue?.venue_id ??
    invoice?.venue_id ??
    invoice?.venueId ??
    invoice?.venue?.id ??
    invoice?.venue?.venue_id;

  const details = item?.details || invoice?.details || item?.services || [];
  const detailsArray = Array.isArray(details) ? details : [details];
  const servicesLabel = detailsArray
    .map((detail: any) => {
      if (!detail) return null;
      if (typeof detail === "string" || typeof detail === "number") {
        return String(detail);
      }
      return (
        detail.title ||
        detail.service ||
        detail.name ||
        detail.service_name ||
        detail.service_id ||
        detail.serviceId ||
        null
      );
    })
    .filter(Boolean)
    .join(", ");

  return {
    ...enriched,
    id: String(
      item?.id ||
        invoice?.id ||
        item?.invoice_id ||
        invoice?.invoice_id ||
        item?._id ||
        ""
    ),
    Invoice_number:
      item?.Invoice_number ||
      invoice?.Invoice_number ||
      item?.invoice_number ||
      invoice?.invoice_number ||
      item?.InvoiceNumber ||
      "",
    client_name:
      item?.client_name ||
      invoice?.client_name ||
      item?.client?.name ||
      item?.client?.full_name ||
      item?.client?.fullName ||
      item?.client?.email ||
      item?.email ||
      "Unknown Client",
    mobile:
      item?.mobile ||
      invoice?.mobile ||
      item?.client?.mobile ||
      item?.phone ||
      item?.phone_number ||
      "",
    invoice_date:
      item?.invoice_date ||
      invoice?.invoice_date ||
      item?.created_at ||
      invoice?.created_at ||
      "",
    created_at: item?.created_at || invoice?.created_at || "",
    venue_name: venueName,
    venue_id:
      venueIdCandidate !== undefined && venueIdCandidate !== null
        ? String(venueIdCandidate)
        : undefined,
    branch: branchName,
    status: resolveStatusLabel(enriched),
    method: resolvePaymentMethodLabel(enriched),
    services: servicesLabel,
    total_amount: item?.total_amount || invoice?.total_amount,
    net_amount: item?.net_amount || invoice?.net_amount,
    notes: item?.notes || invoice?.notes || "",
    business_id:
      item?.business_id ||
      invoice?.business_id ||
      item?.businessId ||
      invoice?.businessId ||
      item?.branch_id ||
      invoice?.branch_id,
  };
};

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) return null;
  const [, day, month, year, hour, minute, second] = match;
  const d = Number(day);
  const m = Number(month) - 1;
  const y = Number(year);
  const h = Number(hour || "0");
  const min = Number(minute || "0");
  const sec = Number(second || "0");
  const date = new Date(y, m, d, h, min, sec);
  return Number.isNaN(date.getTime()) ? null : date;
};
const AdminInvoices: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "finance";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [invoices, setInvoices] = useState<AdminFinanceRow[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = useState<
    string | number | null
  >(null);
  const [invoiceDateRange, setInvoiceDateRange] = useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
  });
  const [invoiceStatusFilter, setInvoiceStatusFilter] =
    useState<string>("all");
  const [invoiceServices, setInvoiceServices] = useState<any[]>([]);
  const [invoiceClients, setInvoiceClients] = useState<InvoiceClientOption[]>(
    []
  );
  const [venues, setVenues] = useState<{ id: string; name: string }[]>([]);
  const [venueFilter, setVenueFilter] = useState<string>("all");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  const handleFinanceNav = (route: string) => {
    if (getCachedPinAccess()) {
      navigate(route);
      return;
    }
    setPendingRoute(route);
    setPinDialogOpen(true);
  };

  const handlePinVerified = () => {
    if (pendingRoute) {
      navigate(pendingRoute);
    }
    setPendingRoute(null);
  };

  const handlePinClose = () => {
    setPinDialogOpen(false);
    setPendingRoute(null);
  };

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await getAllVenues();
        const mapped = (Array.isArray(response) ? response : [])
          .map((venue: any) => {
            const id =
              venue?.id ??
              venue?._id ??
              venue?.venue_id ??
              venue?.venueId ??
              venue?.user_id;
            if (!id) return null;
            const name =
              pickNonEmptyString(
                venue?.name,
                venue?.venue_name,
                venue?.title
              ) || `Venue ${id}`;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];
        setVenues(mapped);
      } catch (error) {
        console.error("Error fetching venues:", error);
        setVenues([]);
      }
    };
    fetchVenues();
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const adminId = getUserId();
      const response = await getAllOwnerInvoices(adminId);
      const invoicesData =
        response?.data?.invoices ||
        response?.invoices ||
        (Array.isArray(response) ? response : []);
      const normalized = (Array.isArray(invoicesData) ? invoicesData : []).map(
        (item: any, index: number) => {
          const row = normalizeInvoiceRow(item);
          if (!row.id || row.id === "undefined") {
            row.id = `invoice-owner-${adminId || "unknown"}-${index}`;
          }
          return row;
        }
      );
      setInvoices(normalized);
    } catch (error) {
      console.error("Error fetching owner invoices:", error);
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredFinance = useMemo(() => {
    const q = search.trim().toLowerCase();
    const venueNameById = new Map(venues.map((v) => [v.id, v.name]));
    const selectedVenueName =
      venueFilter !== "all" ? venueNameById.get(String(venueFilter)) : undefined;

    return invoices.filter((row) => {
      if (invoiceDateRange) {
        const rowDate = parseDateValue(
          row.created_at || row.invoice_date || ""
        );
        if (rowDate) {
          let startDate: Date | null = null;
          let endDate: Date | null = null;
          if (Array.isArray(invoiceDateRange)) {
            startDate = parseDateValue(invoiceDateRange[0] as any);
            endDate = parseDateValue(invoiceDateRange[1] as any);
          } else {
            startDate = parseDateValue((invoiceDateRange as any).start);
            endDate = parseDateValue((invoiceDateRange as any).end);
          }

          if (startDate && rowDate < startDate) return false;
          if (endDate && rowDate > endDate) return false;
        }
      }

      if (invoiceStatusFilter && invoiceStatusFilter !== "all") {
        const rowStatus = row.status;
        if (rowStatus !== invoiceStatusFilter) return false;
      }

      if (venueFilter && venueFilter !== "all") {
        const rowVenueId = row.venue_id ? String(row.venue_id) : "";
        if (rowVenueId) {
          if (rowVenueId !== String(venueFilter)) return false;
        } else if (selectedVenueName) {
          const rowVenueName = String(row.venue_name || "").toLowerCase();
          if (rowVenueName !== selectedVenueName.toLowerCase()) return false;
        } else {
          return false;
        }
      }

      if (!q) return true;

      const haystack = [
        row.client_name,
        row.mobile,
        row.venue_name,
        row.branch,
        row.status,
        row.method,
        row.total_amount,
        row.net_amount,
        row.notes,
        row.services,
      ]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [
    invoices,
    invoiceDateRange,
    invoiceStatusFilter,
    search,
    venueFilter,
    venues,
  ]);

  const paidNetTotal = useMemo(() => {
    return filteredFinance
      .filter((row) => {
        const statusId = Number(
          (row as any).status_id ??
            row.invoice?.status_id ??
            (row as any).status?.id
        );
        if (Number.isFinite(statusId)) {
          return statusId === 1;
        }
        const statusLabel = pickNonEmptyString(
          row.status,
          row.invoice?.status,
          row.invoice?.status_name,
          row.invoice?.status_label
        );
        return statusLabel ? statusLabel.trim().toLowerCase() === "paid" : false;
      })
      .reduce((sum: number, row: any) => {
        const netAmount = Number(row.net_amount ?? row.total_amount ?? 0);
        return sum + netAmount;
      }, 0);
  }, [filteredFinance]);

  const fetchFinance = useCallback(
    async (params: Params<AdminFinanceRow>): Promise<IPageable<AdminFinanceRow>> => ({
      content: filteredFinance,
      number: params.page || 0,
      size: filteredFinance.length || 1,
      totalElements: filteredFinance.length,
      totalPages: 1,
    }),
    [filteredFinance]
  );

  const exportFinanceCsv = useCallback(() => {
    if (!invoices || invoices.length === 0) return;
    const keys = [
      "client_name",
      "mobile",
      "invoice_date",
      "venue_name",
      "branch",
      "status",
      "method",
      "total_amount",
      "net_amount",
      "notes",
      "services",
    ];
    const header = keys.join(",");
    const lines = invoices.map((r: any) =>
      keys
        .map((k) => {
          const v = r[k] ?? "";
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [invoices]);

  const openInvoiceEditForId = useCallback(async (id: string | number) => {
    try {
      const response = await getInvoiceById(String(id));
      const invoiceData = response?.data || response;
      const rowBusinessId =
        invoiceData.business_id ||
        invoiceData.invoice?.business_id ||
        invoiceData.businessId;

      if (rowBusinessId) {
        try {
          const servicesResponse = await getAllServices(String(rowBusinessId));
          const servicesData =
            servicesResponse?.data?.services ||
            servicesResponse?.services ||
            (Array.isArray(servicesResponse) ? servicesResponse : []);
          setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
        } catch (error) {
          console.error("Error fetching services for business:", error);
          setInvoiceServices([]);
        }

        try {
          const clientsResponse = await getAllClients(String(rowBusinessId));
          let clientsData: any[] = [];
          if (clientsResponse?.users) {
            clientsData = clientsResponse.users;
          } else if (clientsResponse?.clients) {
            clientsData = clientsResponse.clients;
          } else if (clientsResponse?.data?.clients) {
            clientsData = clientsResponse.data.clients;
          } else if (clientsResponse?.data?.users) {
            clientsData = clientsResponse.data.users;
          } else if (Array.isArray(clientsResponse?.data)) {
            clientsData = clientsResponse.data;
          } else if (Array.isArray(clientsResponse)) {
            clientsData = clientsResponse;
          }

          const mappedClients: InvoiceClientOption[] = (
            Array.isArray(clientsData) ? clientsData : []
          )
            .map((c: any) => {
              const clientId =
                c?.value ??
                c?.id ??
                c?.client_id ??
                c?._id ??
                c?.user_id ??
                c?.clientId;
              if (!clientId) return null;
              const name =
                c?.label ??
                c?.name ??
                c?.fullName ??
                c?.full_name ??
                c?.client_name ??
                c?.email ??
                `Client ${clientId}`;
              const email = c?.email ?? c?.client_email ?? c?.user?.email ?? "";
              const mobile =
                c?.mobile ??
                c?.phone ??
                c?.phone_number ??
                c?.contact_number ??
                c?.user?.mobile ??
                "";
              return {
                id: String(clientId),
                name: String(name),
                email,
                mobile,
              } as InvoiceClientOption;
            })
            .filter(Boolean) as InvoiceClientOption[];

          setInvoiceClients(mappedClients);
        } catch (error) {
          console.error("Error fetching clients for business:", error);
          setInvoiceClients([]);
        }
      } else {
        setInvoiceServices([]);
        setInvoiceClients([]);
      }

      setEditingInvoice(invoiceData);
      setInvoiceEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  }, []);

  const handleInvoiceDelete = useCallback(async () => {
    if (!invoiceToDeleteId) return;
    try {
      await deleteInvoice(String(invoiceToDeleteId));
      await fetchInvoices();
    } catch (error) {
      console.error("Delete invoice failed", error);
    } finally {
      setInvoiceConfirmOpen(false);
      setInvoiceToDeleteId(null);
    }
  }, [fetchInvoices, invoiceToDeleteId]);

  const handleInvoicePdf = useCallback(
    (id: string | number) => {
      const match = invoices.find((row) => String(row.id) === String(id));
      const branchId =
        match?.business_id ||
        match?.invoice?.business_id ||
        match?.businessId;
      if (!branchId) {
        toast.error("Missing venue id for invoice PDF");
        return;
      }
      navigate(`/dashboard/${branchId}/invoicepdf/${id}`);
    },
    [invoices, navigate]
  );

  const invoiceActions = Actions({
    handleOpenEditModal: { handler: openInvoiceEditForId },
    onDelete: {
      handler: (id: number | string) => {
        setInvoiceToDeleteId(id);
        setInvoiceConfirmOpen(true);
      },
    },
    onPdf: {
      handler: (id: number | string) => handleInvoicePdf(id),
    },
  });
  const invoiceFormFields: Field[] = [
    {
      name: "clientName",
      label: "Client Name",
      colSpan: 1,
      customRender: (value: string, onChange: (v: string) => void, helpers) => {
        const invoiceClientsFromRows = invoices
          .map((inv: any) =>
            inv.client_id && inv.client_name
              ? {
                  id: inv.client_id,
                  name:
                    inv.client_name ||
                    inv.client?.name ||
                    inv.client?.full_name ||
                    inv.client?.fullName,
                  email: inv.client?.email || inv.email,
                  mobile: inv.client?.mobile || inv.mobile,
                }
              : null
          )
          .filter(Boolean) as InvoiceClientOption[];

        const mergedClients = [
          ...(Array.isArray(invoiceClients) ? invoiceClients : []),
          ...invoiceClientsFromRows,
        ].reduce((acc, c: any) => {
          const clientId =
            c?.id ??
            c?.value ??
            c?.client_id ??
            c?._id ??
            c?.user_id ??
            c?.clientId;
          if (!clientId) return acc;
          const key = String(clientId);
          if (!acc.has(key)) {
            acc.set(key, {
              id: key,
              name:
                c?.name ||
                c?.label ||
                c?.client_name ||
                c?.fullName ||
                c?.full_name ||
                c?.email ||
                `Client ${key}`,
              email: c?.email || "",
              mobile:
                c?.mobile ||
                c?.phone ||
                c?.phoneNumber ||
                c?.mobileNumber ||
                "",
            });
          }
          return acc;
        }, new Map<string, InvoiceClientOption>());

        const clientList: InvoiceClientOption[] = Array.from(
          mergedClients.values()
        );

        const options = clientList.map((client: any) => ({
          label: client.name,
          value: client.id,
        }));
        return (
          <>
            <CustomLabel
              sx={{ p: 0.1 }}
              text={"Client Name"}
              color={COLORS.typographyColor}
              fontWeight={500}
              fontSize={14}
            />
            <CustomSelect
              label="Client Name"
              value={value || ""}
              options={options}
              onChange={(v: any) => {
                onChange(String(v || ""));
                if (v) {
                  const selected = clientList.find(
                    (c) => String(c.id) === String(v)
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || ""
                    );
                    return;
                  }
                  getClientById(String(v))
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue("email", client.user.email || "");
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || ""
                        );
                      }
                    })
                    .catch(console.error);
                }
              }}
              fullWidth
              placeholder="Select..."
            />
          </>
        );
      },
    },
    { name: "email", label: "Email" },
    { name: "phoneNumber", label: "Phone Number" },
    { name: "invoice_date", label: "Date", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: ["paid", "cancelled", "unpaid"],
    },
    {
      name: "paymentMethod",
      label: "Payment method",
      type: "dropdown",
      options: ["cash", "card", "bank transfer"],
    },
    { name: "notes", label: "Notes", colSpan: 2 },
    {
      name: "items",
      label: "Items",
      colSpan: 2,
      customRender: (_value: any, onChange: (v: any) => void, helpers) => (
        <InvoiceItemsEditor
          value={_value}
          onChange={onChange}
          vatValue={helpers?.values?.vat || 0}
          onVatChange={(v) => helpers?.setFieldValue("vat", v)}
          discountValue={helpers?.values?.discount || 0}
          onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
          isEditMode={!!editingInvoice}
          servicesList={invoiceServices}
        />
      ),
    },
  ];

  const handleInvoiceSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        const services = (Array.isArray(values.items) ? values.items : []).map(
          (item: any) => ({
            service_id: item.serviceId || String(item.service),
            quantity: item.quantity,
            amount: item.unitCost,
            discount: item.discount,
            net_amount: item.totalPrice,
          })
        );

        const totalAmount = services.reduce(
          (sum: number, item: any) => sum + Number(item.net_amount || 0),
          0
        );
        const discountPercent = Number(values.discount) || 0;
        const discountAmount =
          Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
        const amountAfterDiscount = Math.max(0, totalAmount - discountAmount);
        const vatPercent = Number(values.vat) || 0;
        const vatAmount =
          Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) / 100;
        const netAmount =
          Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

        const businessIdToUse =
          editingInvoice?.business_id ||
          editingInvoice?.invoice?.business_id ||
          editingInvoice?.businessId;

        const invoiceId =
          editingInvoice?.invoice?.id ||
          editingInvoice?.id ||
          editingInvoice?.invoice_id ||
          editingInvoice?.invoice?.invoice_id;

        const payload = {
          client_id: values.clientName,
          appointment_id: values.appointment_id || "1",
          business_id: businessIdToUse,
          status_id:
            values.status === "paid" ? 1 : values.status === "unpaid" ? 2 : 3,
          invoice_number: values.invoice_number || 5967,
          total_amount: totalAmount,
          discount: discountPercent,
          payment_method_id:
            values.paymentMethod === "card"
              ? 1
              : values.paymentMethod === "cash"
              ? 2
              : 3,
          created_by: getUserId() || "1",
          created_at: new Date().toISOString(),
          Vat: vatPercent,
          net_amount: String(netAmount),
          services,
          notes: values.notes,
          invoice_date: values.invoice_date,
          id: invoiceId ? String(invoiceId) : undefined,
        };

        if (editingInvoice && invoiceId) {
          await updateInvoice(String(invoiceId), payload);
        } else {
          await createInvoice(payload);
        }
        await fetchInvoices();
        setInvoiceEditOpen(false);
        setEditingInvoice(null);
      } catch (error) {
        console.error("Error saving invoice:", error);
      }
    },
    [editingInvoice, fetchInvoices]
  );

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    { key: "finance", label: "Invoices", icon: <AccountBalanceWalletOutlinedIcon /> },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        bgcolor: COLORS.MainBackground,
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <Box
        sx={{
          width: sidebarCollapsed ? 72 : 240,
          flexShrink: 0,
          borderRight: "1px solid #eee",
          bgcolor: COLORS.WhiteBackground,
          py: 2,
          px: sidebarCollapsed ? 1 : 1.5,
          transition: "width 0.2s ease",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" fontWeight={700} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "expenses") {
                      handleFinanceNav("/all-data/expenses");
                    } else if (item.key === "reports") {
                      handleFinanceNav("/all-data/reports");
                    }
                  }}
                  sx={{
                    mb: 0.8,
                    mx: 0.2,
                    borderRadius: 2,
                    minHeight: 44,
                    px: sidebarCollapsed ? 1.4 : 2,
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                    color: selected ? COLORS.MainColor : "text.secondary",
                    bgcolor: selected
                      ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.08)"
                      : "transparent",
                    "&:hover": {
                      bgcolor: selected
                        ? COLORS.SidebarActiveBg || "rgba(25,118,210,0.12)"
                        : "rgba(0,0,0,0.03)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 0.5, sm: 1 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <CustomLabel
          text={activeTabLabel}
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 1 }}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...venues.map((venue) => ({
                  label: venue.name,
                  value: venue.id,
                })),
              ],
              value: venueFilter,
              onChange: (v: any) => {
                const next =
                  v === null || v === undefined || v === "" ? "all" : String(v);
                setVenueFilter(next);
              },
              placeholder: "Venue",
              label: "Venue",
              size: "small",
              searchable: false,
              clearable: true,
              labelBgColor: COLORS.WhiteBackground,
              sx: { width: 200 },
            }}
            dateRangeConfig={{
              value: invoiceDateRange ?? undefined,
              onChange: (v: any) => setInvoiceDateRange(v),
              fullWidth: false,
            }}
            selectConfig2={{
              options: [
                { label: "All", value: "all" },
                { label: "Paid", value: "Paid" },
                { label: "Unpaid", value: "Unpaid" },
                { label: "Cancelled", value: "Cancelled" },
              ],
              value: invoiceStatusFilter,
              onChange: (v: any) => setInvoiceStatusFilter(v),
              placeholder: "Status",
              label: "Status",
              size: "small",
              searchable: false,
              clearable: true,
              labelBgColor: COLORS.WhiteBackground,
              sx: { width: 180 },
            }}
            buttonConfig={[
              {
                label: "Export CSV",
                onClick: () => exportFinanceCsv(),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminFinanceRow>
              filters={{ page: 0, size: 10, search }}
              clientSidePagination
              fields={[
                { key: "client_name", header: "Client Name" },
                { key: "mobile", header: "Phone Number" },
                { key: "invoice_date", header: "Date" },
                { key: "venue_name", header: "Venue" },
                { key: "branch", header: "Branch" },
                { key: "status", header: "Status" },
                { key: "method", header: "Payment Method" },
                { key: "services", header: "Services" },
                { key: "net_amount", header: "Net Amount" },
                { key: "notes", header: "Notes" },
              ]}
              fetchFunc={fetchFinance}
              loading={loadingInvoices}
              actions={invoiceActions}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: COLORS.WhiteBackground,
              borderRadius: "0 0 10px 10px",
              padding: "16px",
              marginTop: "-10px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomLabel
              text="Total Net Amount:"
              sx={{ color: COLORS.typographyColor }}
            />
            <CustomLabel
              text={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(paidNetTotal)}
              fontWeight="bold"
              sx={{ color: COLORS.MainColor }}
            />
          </Box>
        </Box>
      </Box>

      <GenericFormDialog
        open={invoiceEditOpen}
        onClose={() => {
          setInvoiceEditOpen(false);
          setEditingInvoice(null);
        }}
        title="Edit Invoice"
        fields={invoiceFormFields}
        initialValues={
          editingInvoice
            ? {
                clientName:
                  editingInvoice.invoice?.client_id ||
                  editingInvoice.client_id,
                email: editingInvoice.client?.email || "",
                phoneNumber: editingInvoice.client?.mobile || "",
                invoice_date: (() => {
                  const date =
                    editingInvoice.invoice?.created_at ||
                    editingInvoice.created_at ||
                    editingInvoice.invoice?.invoice_date ||
                    editingInvoice.invoice_date;
                  if (!date) return "";
                  const dateObj = new Date(date);
                  return dateObj.toISOString().split("T")[0];
                })(),
                status: resolveStatusFormValue(
                  pickNonEmptyString(
                    editingInvoice.status,
                    editingInvoice.invoice?.status,
                    editingInvoice.invoice?.status_name,
                    editingInvoice.invoice?.status_label
                  ),
                  editingInvoice.invoice?.status_id ??
                    editingInvoice.status_id ??
                    editingInvoice.invoice?.status?.id ??
                    editingInvoice.status?.id
                ),
                paymentMethod:
                  (editingInvoice.invoice?.payment_method_id ||
                    editingInvoice.payment_method_id) === 1
                    ? "card"
                    : (editingInvoice.invoice?.payment_method_id ||
                        editingInvoice.payment_method_id) === 2
                    ? "cash"
                    : "bank transfer",
                notes:
                  editingInvoice.invoice?.notes ||
                  editingInvoice.notes ||
                  "",
                items: editingInvoice.details
                  ? editingInvoice.details.map((s: any, index: number) => {
                      const detailServiceId =
                        s.service_id ?? s.serviceId ?? s.serviceID;
                      const normalizedServiceId =
                        detailServiceId !== undefined &&
                        detailServiceId !== null
                          ? String(detailServiceId)
                          : undefined;
                      let matchedService = invoiceServices.find(
                        (srv) =>
                          String(
                            srv.id || srv._id || srv.service_id || ""
                          ) === String(normalizedServiceId || "")
                      );

                      if (!matchedService && s.title) {
                        matchedService = invoiceServices.find((srv) => {
                          const serviceNumber = srv.title
                            ? String(srv.title).match(/^\d+/)?.[0]
                            : null;
                          const detailTitle = String(s.title).trim();
                          return (
                            serviceNumber === detailTitle ||
                            srv.title === detailTitle
                          );
                        });
                      }

                      const matchedServiceId =
                        matchedService?.id ||
                        matchedService?._id ||
                        matchedService?.service_id;
                      const resolvedServiceId =
                        matchedServiceId ||
                        normalizedServiceId ||
                        undefined;

                      let serviceValue;
                      if (resolvedServiceId) {
                        serviceValue = String(resolvedServiceId);
                      } else if (matchedService && matchedService.title) {
                        const titleMatch = matchedService.title.match(/^\d+/);
                        if (titleMatch) {
                          serviceValue = parseInt(titleMatch[0], 10);
                        } else {
                          serviceValue = matchedService.title;
                        }
                      } else if (s.title) {
                        const parsedTitle = parseInt(s.title, 10);
                        serviceValue = !Number.isNaN(parsedTitle)
                          ? parsedTitle
                          : s.title;
                      } else {
                        serviceValue = s.service_id || 60;
                      }

                      return {
                        id: index + 1,
                        service: serviceValue,
                        serviceId: resolvedServiceId,
                        quantity: s.quantity,
                        unitCost: s.amount,
                        discount: s.discount,
                        totalPrice: s.net_amount,
                      };
                    })
                  : [],
                invoice_number:
                  editingInvoice.invoice?.Invoice_number ||
                  editingInvoice.invoice?.invoice_number ||
                  editingInvoice.Invoice_number ||
                  editingInvoice.invoice_number,
                appointment_id:
                  editingInvoice.invoice?.appointment_id ||
                  editingInvoice.appointment_id,
                net_amount:
                  editingInvoice.invoice?.net_amount || editingInvoice.net_amount,
                total_amount:
                  editingInvoice.invoice?.total_amount ||
                  editingInvoice.total_amount,
                vat: String(
                  editingInvoice.invoice?.Vat || editingInvoice.Vat || 0
                ),
                discount: String(
                  editingInvoice.invoice?.discount ||
                    editingInvoice.discount ||
                    0
                ),
              }
            : { vat: "0", discount: "0", paymentMethod: "cash" }
        }
        onSubmit={handleInvoiceSubmit}
      />

      <ConfirmDialog
        open={invoiceConfirmOpen}
        onClose={() => {
          setInvoiceConfirmOpen(false);
          setInvoiceToDeleteId(null);
        }}
        onConfirm={handleInvoiceDelete}
        title="Confirm delete"
        message="Are you sure you want to delete this invoice?"
      />
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={handlePinClose}
        onVerified={handlePinVerified}
      />
    </Box>
  );
};

export default AdminInvoices;
